import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Setup axios interceptor for auth
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        config.headers.Authorization = `Bearer ${storedToken}`;
      }
      return config;
    });

    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  // Load user on mount if token exists
  useEffect(() => {
    const loadUser = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error('Failed to load user:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    
    // Fetch full user with company relation
    const meResponse = await axios.get(`${API_URL}/auth/me`);
    setUser(meResponse.data);
    
    return meResponse.data;
  }, []);

  const register = useCallback(async (data) => {
    const response = await axios.post(`${API_URL}/auth/register`, data);
    const { access_token } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    
    // Fetch full user with company relation
    const meResponse = await axios.get(`${API_URL}/auth/me`);
    setUser(meResponse.data);
    
    return meResponse.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  const switchCompany = useCallback(async (companyId) => {
    const response = await axios.post(`${API_URL}/auth/switch-company/${companyId}`);
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    // Reload current user with new context
    const meResponse = await axios.get(`${API_URL}/auth/me`);
    setUser(meResponse.data);
    return meResponse.data;
  }, []);

  const switchToPlatform = useCallback(async () => {
    const response = await axios.post(`${API_URL}/auth/switch-to-platform`);
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    const meResponse = await axios.get(`${API_URL}/auth/me`);
    setUser(meResponse.data);
    return meResponse.data;
  }, []);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    switchCompany,
    switchToPlatform,
    activeCompanyId: user?.company_id,
    realCompanyId: user?.real_company_id || null,
    isPlatformAdmin: user?.role === 'admin' && !user?.real_company_id,
    isImpersonating: !!(user?.role === 'admin' && user?.company_id),
    isAdmin: user?.role === 'admin',
    isJefatura: user?.role === 'jefatura' || user?.role === 'admin',
    isSolucionador: user?.role === 'solucionador' || user?.role === 'jefatura' || user?.role === 'admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
